import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSupabaseClient } from '@supabase/auth-helpers-react';

interface EmailTemplate {
  title: string;
  subject: string;
  body: string;
}

interface MarketingAsset {
  title: string;
  description: string;
  imageUrl: string;
  downloadUrl: string;
  dimensions?: string;
}

interface VideoAsset {
  title: string;
  description: string;
  thumbnailUrl: string;
  videoUrl: string;
  duration: string;
}

export default function Affiliates() {
  const supabase = useSupabaseClient();
  const [clickbankId, setClickbankId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMonths, setSelectedMonths] = useState(12);
  const [conversionRate, setConversionRate] = useState(100);
  const [showFreeAccessForm, setShowFreeAccessForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [freeAccessForm, setFreeAccessForm] = useState({
    email: '',
    website: '',
    twitter: '',
    facebook: '',
    instagram: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const affiliateLink = `https://hop.clickbank.net/?affiliate=${clickbankId || 'xxxx'}&vendor=betoracle`;

  // Performance metrics placeholders
  const performanceMetrics = {
    customerLifetimeValue: '£139.93', // Placeholder: £19.99 * 12 months
    averageEarningsPerAffiliate: '£39.98', // Placeholder
    refundRate: '4.2%' // Placeholder
  };

  // Marketing assets placeholders
  const bannerAds: MarketingAsset[] = [
    {
      title: 'Leaderboard Banner',
      description: 'Perfect for website headers',
      imageUrl: '/placeholder-728x90.png',
      downloadUrl: '#',
      dimensions: '728x90'
    },
    {
      title: 'Medium Rectangle',
      description: 'Ideal for sidebar placement',
      imageUrl: '/placeholder-300x250.png',
      downloadUrl: '#',
      dimensions: '300x250'
    },
    {
      title: 'Wide Skyscraper',
      description: 'Great for sidebar placement',
      imageUrl: '/placeholder-160x600.png',
      downloadUrl: '#',
      dimensions: '160x600'
    }
  ];

  const socialMediaAssets: MarketingAsset[] = [
    {
      title: 'Facebook Post Template',
      description: 'Optimized for Facebook feed',
      imageUrl: '/placeholder-fb.png',
      downloadUrl: '#'
    },
    {
      title: 'Twitter Post Template',
      description: 'Perfect for Twitter sharing',
      imageUrl: '/placeholder-twitter.png',
      downloadUrl: '#'
    },
    {
      title: 'Instagram Post Template',
      description: 'Square format for Instagram',
      imageUrl: '/placeholder-instagram.png',
      downloadUrl: '#'
    }
  ];

  const productScreenshots: MarketingAsset[] = [
    {
      title: 'Dashboard Overview',
      description: 'Main dashboard interface showing key metrics and controls',
      imageUrl: '/videos/system/Overview-screenshot.png',
      downloadUrl: '/videos/system/Overview-screenshot.png'
    },
    {
      title: "Today's Races",
      description: 'View and analyze qualifying races for the day',
      imageUrl: '/videos/system/Races-screenshot.png',
      downloadUrl: '/videos/system/Races-screenshot.png'
    },
    {
      title: 'Performance Tracking',
      description: 'Track and analyze your betting performance over time',
      imageUrl: '/videos/system/Performance-screenshot.png',
      downloadUrl: '/videos/system/Performance-screenshot.png'
    }
  ];

  const videoResources: VideoAsset[] = [
    {
      title: 'System Overview',
      description: 'A comprehensive walkthrough of Horse Racing Oracle and how it works',
      thumbnailUrl: '/videos/thumbnails/lesson1.png',
      videoUrl: '/videos/system/HRO System - Dashboard.mp4',
      duration: '5:30'
    },
    {
      title: "Today's Races",
      description: 'How to view and analyze qualifying races for the day',
      thumbnailUrl: '/videos/thumbnails/lesson2.png',
      videoUrl: '/videos/system/HRO - Races.mp4',
      duration: '4:15'
    },
    {
      title: 'Staking Calculator',
      description: 'Learn how to use the staking calculator for optimal betting',
      thumbnailUrl: '/videos/thumbnails/lesson3.png',
      videoUrl: '/videos/system/HRO - Staking Calculator.mp4',
      duration: '3:45'
    },
    {
      title: 'Recording Bets',
      description: 'Step-by-step guide to recording your bets and results',
      thumbnailUrl: '/videos/thumbnails/lesson4.png',
      videoUrl: '/videos/system/HRO - Recording Bets.mp4',
      duration: '6:20'
    },
    {
      title: 'Tracking Performance',
      description: 'How to track and analyze your betting performance',
      thumbnailUrl: '/videos/thumbnails/lesson5.png',
      videoUrl: '/videos/system/HRO - Tracking Performance.mp4',
      duration: '4:50'
    }
  ];

  // Email templates (existing code)
  const emailTemplates: EmailTemplate[] = [
    {
      title: "Introduction Email",
      subject: "Transform Your Horse Racing Results with This Proven System",
      body: `Hi there,

I wanted to share an incredible horse racing system I've discovered - Horse Racing Oracle.

This is a complete horse racing system that:
• Uses proven statistical analysis to identify high-probability winners
• Focuses on strong favorites with clear advantages
• Implements strict qualification criteria for race selection
• Includes professional staking plans to maximise profits
• Provides comprehensive performance tracking

What makes this system special is its consistent, methodical approach. It takes the emotion out of betting and replaces it with data-driven decisions.

Check it out here: ${affiliateLink}

They offer a 7 day free trial and 14-day money-back guarantee, so you can try it risk-free.

Let me know if you have any questions!

Best regards`
    },
    {
      title: "Benefits Focus Email",
      subject: "Stop Gambling - Start Profiting from Horse Racing",
      body: `Hello,

Are you tired of inconsistent results from horse racing? I've been using a system that's changed my entire approach to racing.

Horse Racing Oracle is a complete betting system that delivers:
• Carefully selected races that meet strict qualifying criteria
• Clear, easy-to-follow selection process
• Professional money management strategies
• Detailed tracking and analysis tools
• Step-by-step implementation guide

The system is based on years of statistical analysis and real-world testing. It's designed for both newcomers and experienced bettors who want a structured, profitable approach to horse racing.

Try it yourself: ${affiliateLink}

With their 7 day free trial and14-day money-back guarantee, you can test the system risk-free.

Best regards`
    },
    {
      title: "Results Focus Email",
      subject: "Professional Horse Racing System Now Available",
      body: `Hi,

I wanted to share a professional horse racing system that's helping people achieve consistent results.

Horse Racing Oracle stands out because it:
• Uses proven statistical methods to identify value bets
• Only bets on races that meet specific qualifying criteria
• Includes a complete staking strategy
• Provides tools for performance tracking
• Offers detailed documentation and support

This isn't about gambling - it's about applying a systematic, professional approach to horse racing.

The system includes:
• Comprehensive selection criteria
• Risk management guidelines
• Performance tracking tools
• Regular system updates
• Full support package

Get started here: ${affiliateLink}

Remember, there's a 7 day free trial and 14-day money-back guarantee if you're not completely satisfied.

Best regards`
    }
  ];

  const calculateCommission = (months: number, conversions: number) => {
    const initialPrice = 19.99;
    const monthlyPrice = 19.99;
    
    const firstMonthCommission = (initialPrice * 0.5) * conversions; // 50% of initial sale
    const recurringCommission = (monthlyPrice * 0.25) * (months - 1) * conversions; // 25% of recurring sales
    
    return {
      firstMonth: firstMonthCommission.toFixed(2),
      recurring: recurringCommission.toFixed(2),
      total: (firstMonthCommission + recurringCommission).toFixed(2)
    };
  };

  const handleFreeAccessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!freeAccessForm.email) {
      setError('Email address is required');
      return;
    }

    try {
      setIsSubmitting(true);
      const { error } = await supabase.from('affiliate_applications').insert({
        email: freeAccessForm.email,
        website: freeAccessForm.website || null,
        twitter_handle: freeAccessForm.twitter || null,
        facebook_handle: freeAccessForm.facebook || null,
        instagram_handle: freeAccessForm.instagram || null,
        user_id: null // explicitly set to null since we don't require authentication
      });

      if (error) throw error;

      setSuccess('Application submitted successfully! We will review it shortly.');
      setShowFreeAccessForm(false);
      setFreeAccessForm({
        email: '',
        website: '',
        twitter: '',
        facebook: '',
        instagram: ''
      });

    } catch (error: any) {
      console.error('Error submitting application:', error.message);
      setError('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyTemplate = (template: EmailTemplate) => {
    const fullText = `Subject: ${template.subject}\n\n${template.body}`;
    navigator.clipboard.writeText(fullText);
  };

  const commissionResults = calculateCommission(selectedMonths, conversionRate);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-block mb-8">
            <img
              src="/HRO-Logo-transparent-white-logo.png"
              alt="Horse Racing Oracle"
              className="h-12"
            />
          </Link>
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Affiliate Program
          </h1>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {['overview', 'resources', 'calculator', 'templates'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Sections */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6 space-y-8">
            
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                {/* Commission Info */}
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                    Earn 50% Commission on First Sale and 25% on Recurring Forever!
                  </h2>
                  <p className="text-gray-600 mb-8">
                    We pay 50% commission on every single initial sale, and 25% commission on every recurring sale, forever! We use the sales platform{' '}
                    <a 
                      href="https://www.clickbank.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      www.clickbank.com
                    </a>
                    . They are a proven affiliate platform.{' '}
                    <a 
                      href="https://www.clickbank.com/affiliates/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      Learn more here
                    </a>
                    .
                  </p>

                  {/* Performance Metrics */}
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-8">
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                      <div className="px-4 py-5 sm:p-6">
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Customer Lifetime Value
                        </dt>
                        <dd className="mt-1 text-3xl font-semibold text-gray-900">
                          {performanceMetrics.customerLifetimeValue}
                        </dd>
                      </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                      <div className="px-4 py-5 sm:p-6">
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Average Earnings per Affiliate (per conversion)
                        </dt>
                        <dd className="mt-1 text-3xl font-semibold text-gray-900">
                          {performanceMetrics.averageEarningsPerAffiliate}
                        </dd>
                      </div>
                    </div>
                    <div className="bg-white overflow-hidden shadow rounded-lg">
                      <div className="px-4 py-5 sm:p-6">
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Refund Rate
                        </dt>
                        <dd className="mt-1 text-3xl font-semibold text-gray-900">
                          {performanceMetrics.refundRate}
                        </dd>
                      </div>
                    </div>
                  </div>

                  {/* Try It Free CTA - More Prominent */}
                  <div className="relative rounded-lg p-6 my-8 bg-gradient-to-br from-gray-900 via-primary-900 to-gray-900 shadow-xl border-4 border-primary-700 flex flex-col items-center text-center overflow-hidden">
                    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_1px_1px,_rgb(255_255_255_/_0.15)_1px,_transparent_0)] [background-size:24px_24px] pointer-events-none" />
                    <div className="absolute top-0 right-0 m-2">
                      <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider bg-white text-primary-700 rounded-full shadow">Exclusive</span>
                    </div>
                    <h3 className="text-2xl font-extrabold text-white mb-2 drop-shadow-lg">
                      Want to Try or Test Horse Racing Oracle Before Promoting?
                    </h3>
                    <p className="text-lg text-white mb-4 font-medium drop-shadow">
                      Get <span className="font-bold underline">free access</span> to test the product and see the results for yourself. No risk, no obligation.
                    </p>
                    <button
                      onClick={() => setShowFreeAccessForm(true)}
                      className="btn btn-primary text-lg px-8 py-3 rounded-full shadow-lg bg-white text-primary-700 font-bold border-2 border-primary-700 hover:bg-primary-700 hover:text-white transition"
                    >
                      Request Free Access
                    </button>
                  </div>
                </div>

                {/* How it Works */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    How Does It Work?
                  </h2>
                  <p className="text-gray-600">
                    You sign up for an account at Clickbank. During the signup process you will receive a "Clickbank ID". 
                    The link that is output from the form is your "hoplink". This is the link that you will promote to 
                    your friends, family, mailing list or online advertising. When someone clicks on that link and buys 
                    one of our products, you automatically receive 50% of the purchase and 25% of the recurring subscription revenue. 
                    It's that simple.
                  </p>
                </div>

                {/* Affiliate Link Generator */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Generate Your Affiliate Link
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="clickbank-id" className="block text-sm font-medium text-gray-700">
                        Your Clickbank ID
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          id="clickbank-id"
                          value={clickbankId}
                          onChange={(e) => setClickbankId(e.target.value)}
                          placeholder="Enter your Clickbank ID"
                          className="input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Your Affiliate Link
                      </label>
                      <div className="mt-1 flex rounded-md shadow-sm">
                        <input
                          type="text"
                          readOnly
                          value={affiliateLink}
                          className="input"
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        Change xxxx to your Clickbank ID so you get paid!
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Resources Tab */}
            {activeTab === 'resources' && (
              <div className="space-y-8">
                {/* Video Resources */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Video Tutorials</h2>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {videoResources.map((video, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="relative">
                          <img 
                            src={video.thumbnailUrl} 
                            alt={video.title} 
                            className="w-full aspect-video object-cover rounded-lg mb-4" 
                          />
                          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm">
                            {video.duration}
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 bg-white bg-opacity-80 rounded-full flex items-center justify-center">
                              <svg className="w-8 h-8 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{video.title}</h3>
                        <p className="text-sm text-gray-500 mb-4">{video.description}</p>
                        <a 
                          href={video.videoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn btn-primary btn-sm w-full"
                        >
                          Watch Video
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Product Screenshots */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Product Screenshots</h2>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {productScreenshots.map((screenshot, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <img 
                          src={screenshot.imageUrl} 
                          alt={screenshot.title} 
                          className="w-full aspect-video object-cover rounded-lg mb-4 shadow-md hover:shadow-lg transition-shadow duration-200"
                        />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{screenshot.title}</h3>
                        <p className="text-sm text-gray-500 mb-4">{screenshot.description}</p>
                        <a 
                          href={screenshot.downloadUrl} 
                          download
                          className="btn btn-primary btn-sm w-full"
                        >
                          Download Screenshot
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Banner Ads */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Banner Ads</h2>
                  <div className="relative">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 opacity-50 blur-[2px]">
                      {bannerAds.map((banner, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <div className="bg-gray-200 w-full h-32 mb-4 rounded flex items-center justify-center">
                            <span className="text-gray-400">Banner Preview</span>
                          </div>
                          <h3 className="text-lg font-medium text-gray-900">{banner.title}</h3>
                          <p className="text-sm text-gray-500 mb-2">{banner.description}</p>
                          <p className="text-sm text-gray-500 mb-4">{banner.dimensions}</p>
                          <button className="btn btn-primary btn-sm w-full" disabled>
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-10 rounded-lg">
                      <div className="bg-primary-600 text-white px-6 py-3 rounded-lg shadow-lg text-lg font-semibold">
                        Coming Soon
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media Templates */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Social Media Templates</h2>
                  <div className="relative">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 opacity-50 blur-[2px]">
                      {socialMediaAssets.map((asset, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <div className="bg-gray-200 w-full h-32 mb-4 rounded flex items-center justify-center">
                            <span className="text-gray-400">Template Preview</span>
                          </div>
                          <h3 className="text-lg font-medium text-gray-900">{asset.title}</h3>
                          <p className="text-sm text-gray-500 mb-4">{asset.description}</p>
                          <button className="btn btn-primary btn-sm w-full" disabled>
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-10 rounded-lg">
                      <div className="bg-primary-600 text-white px-6 py-3 rounded-lg shadow-lg text-lg font-semibold">
                        Coming Soon
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Calculator Tab */}
            {activeTab === 'calculator' && (
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Commission Calculator
                </h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Months
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="24"
                      value={selectedMonths}
                      onChange={(e) => setSelectedMonths(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-sm text-gray-500 mt-1">
                      {selectedMonths} months
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Conversions
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={conversionRate}
                      onChange={(e) => setConversionRate(parseInt(e.target.value))}
                      className="input"
                    />
                  </div>
                  <div className="bg-white rounded-lg p-6 space-y-4">
                    <div>
                      <div className="text-sm text-gray-500">First Month Commission (50%)</div>
                      <div className="text-2xl font-bold text-gray-900">£{commissionResults.firstMonth}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Recurring Commission (25%)</div>
                      <div className="text-2xl font-bold text-gray-900">£{commissionResults.recurring}</div>
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                      <div className="text-sm text-gray-500">Total Estimated Commission</div>
                      <div className="text-3xl font-bold text-primary-600">£{commissionResults.total}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Promotional Email Templates
                </h2>
                <div className="space-y-6">
                  <div className="flex space-x-4 overflow-x-auto pb-2">
                    {emailTemplates.map((template, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedTemplate(index)}
                        className={`px-4 py-2 rounded-md whitespace-nowrap ${
                          selectedTemplate === index
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {template.title}
                      </button>
                    ))}
                  </div>

                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {emailTemplates[selectedTemplate].title}
                      </h3>
                      <button
                        onClick={() => copyTemplate(emailTemplates[selectedTemplate])}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy Template
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Subject Line
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            readOnly
                            value={emailTemplates[selectedTemplate].subject}
                            className="input bg-gray-50"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email Body
                        </label>
                        <div className="mt-1">
                          <textarea
                            readOnly
                            value={emailTemplates[selectedTemplate].body}
                            rows={12}
                            className="input bg-gray-50"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Free Access Form Modal */}
        {showFreeAccessForm && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Request Free Access
                </h3>
                <button
                  onClick={() => setShowFreeAccessForm(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleFreeAccessSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={freeAccessForm.email}
                    onChange={(e) => setFreeAccessForm({...freeAccessForm, email: e.target.value})}
                    className="input"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Website
                  </label>
                  <input
                    type="url"
                    value={freeAccessForm.website}
                    onChange={(e) => setFreeAccessForm({...freeAccessForm, website: e.target.value})}
                    className="input"
                    disabled={isSubmitting}
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Social Media Handles
                  </label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Twitter username (without @)"
                      value={freeAccessForm.twitter}
                      onChange={(e) => setFreeAccessForm({...freeAccessForm, twitter: e.target.value})}
                      className="input"
                      disabled={isSubmitting}
                    />
                    <input
                      type="text"
                      placeholder="Facebook username"
                      value={freeAccessForm.facebook}
                      onChange={(e) => setFreeAccessForm({...freeAccessForm, facebook: e.target.value})}
                      className="input"
                      disabled={isSubmitting}
                    />
                    <input
                      type="text"
                      placeholder="Instagram username (without @)"
                      value={freeAccessForm.instagram}
                      onChange={(e) => setFreeAccessForm({...freeAccessForm, instagram: e.target.value})}
                      className="input"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowFreeAccessForm(false)}
                    className="btn btn-secondary"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Submitting...
                      </span>
                    ) : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 